import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2, Mail, User, Phone, Lock, Eye, EyeOff, UserPlus, MapPin,
  CheckCircle2, Copy, Check, ArrowRight
} from 'lucide-react';
import useAuth from '../../hooks/useAuth';

export default function Register() {
  const { register, completeRegistration, isLoading, error, clearError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(null);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    schoolName: '',
    email: '',
    teacherName: '',
    phone: '',
    location: '',
    password: '',
    confirmPassword: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setLocalError('');
    clearError();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    // Validate password strength
    if (formData.password.length < 8) {
      setLocalError('Password must be at least 8 characters long');
      return;
    }

    const result = await register(formData);
    if (result.success) {
      // Show success screen with class code
      setRegistrationSuccess({
        classCode: result.classCode,
        user: result.user,
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
      });
    }
  };

  const handleCopyCode = async () => {
    if (registrationSuccess?.classCode) {
      try {
        await navigator.clipboard.writeText(registrationSuccess.classCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const handleContinue = () => {
    if (registrationSuccess) {
      completeRegistration(
        registrationSuccess.user,
        registrationSuccess.accessToken,
        registrationSuccess.refreshToken
      );
    }
  };

  const displayError = localError || error;

  // Success Screen
  if (registrationSuccess) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md"
        >
          <div className="card p-8 text-center">
            {/* Success Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle2 className="text-green-600" size={40} />
            </motion.div>

            <h1 className="text-2xl mb-2">Registration Successful!</h1>
            <p className="text-gray-600 mb-8">
              Your school has been registered. Share the class code below with your students.
            </p>

            {/* Class Code Display */}
            <div className="bg-leaf-50 border-2 border-leaf-200 rounded-2xl p-6 mb-6">
              <p className="text-sm font-medium text-leaf-700 mb-2">Your Class Code</p>
              <div className="flex items-center justify-center gap-3">
                <span className="text-4xl font-bold tracking-[0.3em] text-leaf-800 font-mono">
                  {registrationSuccess.classCode}
                </span>
                <button
                  onClick={handleCopyCode}
                  className="p-2 rounded-lg hover:bg-leaf-100 transition-colors"
                  title="Copy class code"
                >
                  {copied ? (
                    <Check size={24} className="text-green-600" />
                  ) : (
                    <Copy size={24} className="text-leaf-600" />
                  )}
                </button>
              </div>
              {copied && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-green-600 mt-2"
                >
                  Copied to clipboard!
                </motion.p>
              )}
            </div>

            {/* Important Note */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8 text-left">
              <p className="text-sm text-amber-800">
                <strong>Important:</strong> Students will need this code to sign in.
                You can always find it in your dashboard settings.
              </p>
            </div>

            {/* Continue Button */}
            <button
              onClick={handleContinue}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              Go to Dashboard
              <ArrowRight size={20} />
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Registration Form
  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        {/* Card */}
        <div className="card p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-leaf-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="text-leaf-600" size={32} />
            </div>
            <h1 className="text-2xl mb-2">Register Your School</h1>
            <p className="text-gray-600">Start your environmental education journey</p>
          </div>

          {/* Error Message */}
          {displayError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
              {displayError}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* School Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                School Name *
              </label>
              <div className="relative">
                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  name="schoolName"
                  value={formData.schoolName}
                  onChange={handleChange}
                  required
                  placeholder="Green Valley Primary School"
                  className="input-field pl-12"
                />
              </div>
            </div>

            {/* Two columns on larger screens */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* School Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  School Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="info@school.com"
                    className="input-field pl-12"
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+254 700 000 000"
                    className="input-field pl-12"
                  />
                </div>
              </div>
            </div>

            {/* Teacher Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Teacher/Patron Name *
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  name="teacherName"
                  value={formData.teacherName}
                  onChange={handleChange}
                  required
                  placeholder="Jane Wanjiku"
                  className="input-field pl-12"
                />
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="Nairobi, Kenya"
                  className="input-field pl-12"
                />
              </div>
            </div>

            {/* Passwords */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password *
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="Min. 8 characters"
                    className="input-field pl-12 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400
                             hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password *
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    placeholder="Confirm password"
                    className="input-field pl-12"
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center gap-2 mt-6"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <UserPlus size={20} />
                  Create Account
                </>
              )}
            </button>
          </form>

          {/* Login Link */}
          <p className="mt-8 text-center text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-leaf-600 hover:text-leaf-700 font-semibold">
              Sign in
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
